<template>
  <!-- Certificate Modal -->
  <div class="modal fade" id="certificateModal" tabindex="-1" role="dialog" aria-labelledby="certificateModalLabel"
    aria-hidden="true">
    <div class="modal-dialog modal-lg modal-dialog-centered" role="document">
      <div class="modal-content certificate-modal-content">
        <div class="modal-header certificate-modal-header">
          <p class="modal-title mb-0 text-white" id="certificateModalLabel">
            <i class="fe fe-award mr-2"></i>
            <span class="h5 mb-0">Certificate of Compliance</span>
          </p>
          <button type="button" class="close text-white" data-dismiss="modal" aria-label="Close">
            <span aria-hidden="true">&times;</span>
          </button>
        </div>

        <div class="modal-body certificate-modal-body">
          <div id="certificatePrintDiv" ref="certificateRef" class="certificate">

            <!-- Header: FCTA logo · agency logo + name · cert meta & QR -->
            <div class="cert-header">
              <div class="cert-header-left">
                <img src="/assets/img/fcta_logo.jpg" alt="FCTA" class="cert-fcta-logo" />
              </div>

              <div class="cert-header-center">
                <img :src="`https://claimify-api.hayokmedicare.ng/image/${agencyImage}`" alt="Agency logo"
                  class="cert-agency-logo" onerror="this.onerror=null; this.src='/claimify_logo.png'" />
                <h2 class="cert-scheme-name">{{ agencyName }}</h2>
              </div>

              <div class="cert-header-right">
                <p class="cert-meta-line"><strong>Certificate No.:</strong> {{ certificateNumber }}</p>
                <p class="cert-meta-line"><strong>Date Issued:</strong> {{ current_date }}</p>
                <qrcode-vue :value="verificationUrl" :size="74" :level="'H'" render-as="svg"></qrcode-vue>
                <p class="cert-qr-caption">Scan to verify this certificate</p>
              </div>
            </div>

            <div class="cert-header-rule"></div>

            <!-- Title -->
            <p class="certificate-eyebrow">Certificate of Compliance</p>
            <div class="certificate-divider">
              <span>&#10022;</span>
            </div>

            <p class="certificate-lead">This is to certify that</p>
            <h1 class="certificate-name">{{ organizationName }}</h1>
            <p class="certificate-body">
              has complied with the requirements of the <strong>{{ agencyName }}</strong>
              regarding the registration of staff and remittance of contributions, with
              <strong>{{ totalEnrollees | numeral("0,0") }}</strong>
              enrolled beneficiar{{ totalEnrollees == 1 ? 'y' : 'ies' }}
              on record as at the date of issue.
            </p>

            <!-- Footer: signature · seal -->
            <div class="certificate-footer">
              <div class="certificate-signature">
                <div class="signature-line"></div>
                <p class="signature-label">Director</p>
                <p class="signature-sub">{{ agencyName }}</p>
              </div>

              <div class="certificate-seal">
                <div class="seal-star">
                  <i class="fe fe-award"></i>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="modal-footer certificate-modal-footer">
          <button type="button" class="btn btn-outline-secondary" data-dismiss="modal">Close</button>
          <button type="button" class="btn btn-outline-info" @click="downloadCertificateImage">
            <i class="fe fe-download mr-1"></i> Download Image
          </button>
          <button type="button" class="btn btn-warning" @click="printCertificate">
            <i class="fe fe-printer mr-1"></i> Print Certificate
          </button>
        </div>
      </div>
    </div>
  </div>
  <!-- End Certificate Modal -->
</template>

<script>
import QRCodeVue from "qrcode.vue";
import moment from "moment";

export default {
  name: "MDACertificate",
  components: {
    qrcodeVue: QRCodeVue,
  },
  props: {
    organizationName: { type: String, default: "" },
    agencyName: { type: String, default: "" },
    agencyImage: { type: String, default: "" },
    totalEnrollees: { type: Number, default: 0 },
  },
  data() {
    return {
      current_date: moment().format("Do MMMM, YYYY"),
    };
  },
  computed: {
    verificationUrl() {
      return `https://app.claimify.ng/verify-organization/${this.$route.params.id}`;
    },
    certificateNumber() {
      const year = moment().format("YYYY");
      return `CERT-${this.$route.params.id}-${year}`;
    },
  },
  methods: {
    printCertificate() {
      const printContents = document.getElementById("certificatePrintDiv").innerHTML;
      const originalContents = document.body.innerHTML;

      // Scoped to this print only — @page can't be scoped by selector, so a
      // global rule here would leak landscape orientation into every other
      // print job in the app (e.g. EnrolleeSlip.vue).
      const printStyle = document.createElement("style");
      printStyle.innerHTML = "@page { size: landscape; margin: 10mm; }";
      document.head.appendChild(printStyle);

      document.body.innerHTML = `<div class="certificate-print-root">${printContents}</div>`;
      window.print();
      document.body.innerHTML = originalContents;
      document.head.removeChild(printStyle);
    },
    async downloadCertificateImage() {
      this.$emit("loading", true);
      try {
        const el = this.$refs.certificateRef;
        const dataUrl = await this.$html2canvas(el, { type: "dataURL" });
        const link = document.createElement("a");
        link.href = dataUrl;
        link.setAttribute("download", `${this.organizationName || "certificate"}.png`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } catch (error) {
        console.error(error);
        this.$toasted.error("Could not generate image. Try Print instead.", {
          position: "top-center",
          duration: 3000,
        });
      } finally {
        this.$emit("loading", false);
      }
    },
  },
};
</script>

<style>
/* ── Certificate ── */
@import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,600;0,700;1,600&family=EB+Garamond:ital,wght@0,400;0,600;1,400&display=swap');

.certificate-modal-content { border-radius: 14px; overflow: hidden; border: none; }
.certificate-modal-header {
    background: linear-gradient(135deg, #1e3a5f 0%, #2563eb 100%);
    border-bottom: none;
    display: flex; align-items: center; justify-content: space-between;
}
.certificate-modal-body { background: #e9e2d0; padding: 34px; }
.certificate-modal-footer { border-top: 1px solid #eee; }

.certificate {
    position: relative;
    background: #fffefb;
    border: 2px solid #c9a227;
    border-radius: 16px;
    padding: 28px 40px 36px;
    font-family: 'EB Garamond', Georgia, serif;
    color: #2b2110;
    text-align: center;
    box-shadow: 0 20px 50px rgba(0, 0, 0, .2);
}

/* Header row: logo | scheme name | cert meta + QR */
.cert-header {
    display: grid;
    grid-template-columns: 120px 1fr 120px;
    align-items: start;
    gap: 12px;
    text-align: left;
}
.cert-header-left { display: flex; justify-content: flex-start; padding-top: 4px; }
.cert-fcta-logo { width: 78px; height: 78px; object-fit: contain; }

.cert-header-center { text-align: center; }
.cert-agency-logo { width: 64px; height: 64px; object-fit: contain; margin-bottom: 6px; }
.cert-scheme-name {
    font-family: 'EB Garamond', serif;
    font-weight: 700;
    font-size: 1.5rem;
    line-height: 1.2;
    color: #0f6b3a;
    margin: 0;
    text-transform: uppercase;
}

.cert-header-right { text-align: right; }
.cert-meta-line {
    margin: 0 0 2px; font-size: .62rem; color: #2b2110; font-family: Georgia, serif;
    white-space: nowrap;
}
.cert-header-right svg { margin: 6px 0 4px; }
.cert-qr-caption { margin: 0; font-size: .58rem; font-style: italic; color: #6b5d3f; max-width: 130px; margin-left: auto; }

.cert-header-rule { height: 1px; background: #c9a227; margin: 16px 0 22px; }

.certificate-eyebrow {
    margin: 0;
    font-family: 'EB Garamond', serif;
    letter-spacing: .28em; text-transform: uppercase;
    font-size: 1.6rem; color: #1c1c1c; font-weight: 700;
}
.certificate-divider { margin: 10px auto 22px; width: 160px; border-top: 1px solid #b8860b; line-height: 0; }
.certificate-divider span { background: #fffefb; padding: 0 12px; position: relative; top: -10px; color: #b8860b; font-size: 1rem; }

.certificate-lead { font-style: italic; font-size: 1.05rem; color: #5a4a25; margin-bottom: 18px; }
.certificate-name {
    font-family: 'Cormorant Garamond', serif;
    font-weight: 700; font-size: 2.6rem; line-height: 1.15;
    color: #1e3a5f; margin: 0 0 22px;
}
.certificate-body { max-width: 620px; margin: 0 auto 40px; font-size: 1.05rem; line-height: 1.75; color: #4a3d20; }
.certificate-body strong { color: #1e3a5f; }

.certificate-footer {
    display: flex; align-items: center; justify-content: space-between;
    margin-top: 20px;
    text-align: left;
}
.certificate-signature { text-align: center; min-width: 200px; }
.signature-line { width: 200px; border-top: 1px solid #4a3d20; margin: 0 auto 6px; }
.signature-label { margin: 0; font-weight: 700; font-size: .85rem; color: #1c1c1c; text-transform: uppercase; }
.signature-sub { margin: 0; font-size: .78rem; color: #4a3d20; }

.certificate-seal { display: flex; justify-content: flex-end; }
.seal-star {
    width: 88px; height: 88px;
    display: flex; align-items: center; justify-content: center;
    color: #fff; font-size: 1.7rem;
    background: radial-gradient(circle at 35% 30%, #e3564a, #c0392b 70%, #96271c);
    clip-path: polygon(
        50% 0%, 61% 12%, 75% 6%, 79% 21%, 93% 21%, 91% 36%,
        100% 45%, 89% 55%, 96% 68%, 82% 71%, 80% 86%, 66% 82%,
        58% 96%, 50% 84%, 42% 96%, 34% 82%, 20% 86%, 18% 71%,
        4% 68%, 11% 55%, 0% 45%, 9% 36%, 7% 21%, 21% 21%,
        25% 6%, 39% 12%
    );
    box-shadow: 0 4px 12px rgba(192, 57, 43, .4);
}

@media print {
    .certificate { box-shadow: none; }
}
</style>
